import { useEffect, useRef, useState } from 'react';
import { Avatar } from './Avatar';
import { listInspirations, markFindsSeen, setInspirationStatus, type Inspiration } from '../lib/crew';
import { track } from '../analytics';

/**
 * What your crew pulled out of what you made, the moment they pull it (10 §10).
 * The pass used to run at three in the morning; it now runs as the card is
 * being pinned up, so this block polls for a few minutes and the find arrives
 * on the screen the person is still looking at. Wherever it is mounted — Home,
 * the reward, the feed — it is the same block, so the find cannot be missed by
 * being on the wrong tab.
 *
 * Nothing is asked of the person here (owner, 2026-09-18). A find is kept the
 * moment it is shown: being handed "Keep / Not this one" about a line you
 * wrote ninety seconds ago is a decision nobody has the standing to make yet,
 * and it was the loudest thing on a screen that is meant to be a reward. The
 * discard path stays in the data model for the UX that will replace this.
 */
export function CrewFinds({ waiting = false, onChange }: { waiting?: boolean; onChange?: () => void }) {
  const [items, setItems] = useState<Inspiration[] | null>(null);
  const seen = useRef(false);
  // the finds that turned up while this block has been open: what it shows
  const arrived = useRef<Set<string>>(new Set());
  // already written back as kept, so a poll that races the write does not do it twice
  const keptIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    let stop = false;
    let tries = 0;
    const tick = async () => {
      try {
        const ins = await listInspirations();
        if (stop) return;
        const fresh = ins.filter((i) => i.status === 'new' && !keptIds.current.has(i.id));
        fresh.forEach((i) => arrived.current.add(i.id));
        setItems(ins);
        if (fresh.length > 0 && !seen.current) {
          // The app has shown them, so the hourly cue must not say they are waiting.
          seen.current = true;
          markFindsSeen().catch(() => {});
          track('overnight_shown', { count: fresh.length });
        }
        // Shown is kept: the person does not have to decide anything.
        if (fresh.length > 0) {
          fresh.forEach((i) => keptIds.current.add(i.id));
          await Promise.all(fresh.map((i) => setInspirationStatus(i.id, 'kept').catch(() => keptIds.current.delete(i.id))));
          if (stop) return;
          track('inspiration_kept', { count: fresh.length, how: 'auto' });
          setItems((prev) => (prev || []).map((x) => (arrived.current.has(x.id) ? { ...x, status: 'kept' } : x)));
          onChange?.();
        }
      } catch { /* offline or rules: the block simply stays quiet */ }
      // Five minutes of looking, then stop: the crew take seconds, and a tab
      // left open all day should not poll all day.
      if (!stop && ++tries < 40) setTimeout(tick, 8000);
    };
    tick();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shown = (items || []).filter((i) => arrived.current.has(i.id));
  if (shown.length === 0) {
    if (!waiting || items === null) return null;
    return (
      <div className="sheet" style={{ gap: 8 }}>
        <div className="row" style={{ gap: 8 }}><Avatar who="the-moon" size={28} /><span className="label">Your crew are reading it…</span></div>
      </div>
    );
  }
  return (
    <div className="sheet" style={{ gap: 10 }}>
      {/* No claim about what the crew saw in it: just where it came from, which
          the person can check against their own words underneath. */}
      <div className="row" style={{ gap: 8 }}><Avatar who="the-moon" size={28} /><span className="label">Kept from your words</span></div>
      {shown.map((i) => (
        <div key={i.id} className="found">
          <span className="label" style={{ color: 'var(--coral-dark)' }}>{i.slot}</span>
          <b>{i.text}</b>
          {i.quote && <span className="hint">from your Day {i.madeFor} words: “{i.quote}”</span>}
        </div>
      ))}
    </div>
  );
}
